import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { api, timeAgo, formatEth, formatNumber } from "@/lib/api";
import { AlertCircle, HelpCircle, Download, Copy, Check, Eye, Server, ChevronRight, ChevronLeft } from "lucide-react";
import HoverTip from "@/components/HoverTip";
import { keccak_256 } from "js-sha3";

// EIP-55 checksum casing for visual parity with Etherscan
function toChecksumAddr(addr) {
  if (!addr) return "";
  const a = addr.toLowerCase().replace(/^0x/, "");
  const hash = keccak_256(a);
  let out = "0x";
  for (let i = 0; i < a.length; i++) {
    const c = a[i];
    if (/[a-f]/.test(c)) out += parseInt(hash[i], 16) >= 8 ? c.toUpperCase() : c;
    else out += c;
  }
  return out;
}
function shortChecksum(addr, left = 8, right = 6) {
  const c = toChecksumAddr(addr);
  if (c.length <= left + right + 3) return c;
  return `${c.slice(0, left)}...${c.slice(-right)}`;
}

const BUILDER_NAMES = ["quasarbuilder", "Titan Builder", "beaverbuild", "BuilderNet", "Eureka Builder", "rsync-builder"];
function minerLabel(addr) {
  if (!addr) return null;
  try {
    const idx = parseInt(addr.slice(-4), 16) % BUILDER_NAMES.length;
    return BUILDER_NAMES[idx];
  } catch { return null; }
}

const InlineCopy = ({ text }) => {
  const [ok, setOk] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1200); }}
      className="inline-flex items-center text-[var(--ks-text-soft)] hover:text-[var(--ks-primary)] ml-1"
      title="Copy"
    >
      {ok ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
};

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [totalReal, setTotalReal] = useState(0);
  const [totalDisplay, setTotalDisplay] = useState(0);
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page") || 1));
  const perPage = 25;
  const [tips, setTips] = useState({
    failed: "Warning! Error encountered during contract execution [Out of gas]",
    pending: "Pending",
  });

  useEffect(() => {
    api.get(`/transactions`, { params: { limit: perPage, skip: (page - 1) * perPage } })
      .then((r) => {
        setItems(r.data.items);
        setTotalReal(r.data.total);
        setTotalDisplay(r.data.total_display || r.data.total);
      });
    api.get(`/settings/tooltips`).then((r) => setTips(r.data)).catch(() => {});
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(totalReal / perPage));
  const paginationCap = 10000; // like Etherscan
  const shownPages = Math.min(totalPages, paginationCap);

  const setPage = (p) => setParams({ page: String(Math.max(1, Math.min(shownPages, p))) });

  const downloadCsv = () => {
    const header = ["hash", "method", "block", "timestamp_utc", "age", "from", "to", "value_eth", "txn_fee_eth", "status"];
    const rows = items.map((t) => [
      t.hash, t.method, t.block_number, new Date(t.timestamp * 1000).toISOString(),
      timeAgo(t.timestamp), t.from_address, t.to_address, t.value_eth, t.fee_eth, t.status,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions-page-${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="ks-card overflow-hidden">
        {/* Header row — Etherscan style */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--ks-border)] flex-wrap gap-3" data-testid="txs-header">
          <div>
            <div className="text-sm text-[var(--ks-text)]">
              More than <span className="font-medium">{formatNumber(totalDisplay)}</span> transactions found
            </div>
            <div className="text-xs text-[var(--ks-text-soft)] mt-0.5">(Showing the last 500k records)</div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={downloadCsv}
              className="inline-flex items-center gap-1.5 border border-[var(--ks-border)] rounded-md px-2.5 py-1 hover:bg-[var(--ks-surface)]"
              data-testid="txs-download"
            >
              <Download size={12} /> Download Page Data
            </button>
            <div className="inline-flex items-center gap-1 border border-[var(--ks-border)] rounded-md overflow-hidden">
              <button type="button" onClick={() => setPage(1)} disabled={page <= 1} className="px-2 py-1 hover:bg-[var(--ks-surface)] disabled:opacity-40" data-testid="pg-first">First</button>
              <button type="button" onClick={() => setPage(page - 1)} disabled={page <= 1} className="px-1.5 py-1 hover:bg-[var(--ks-surface)] disabled:opacity-40 border-l border-[var(--ks-border)]" data-testid="pg-prev"><ChevronLeft size={12}/></button>
              <span className="px-2 py-1 text-[var(--ks-text-soft)] border-l border-[var(--ks-border)]">Page <span className="text-[var(--ks-text)] font-medium">{page}</span> of {shownPages}</span>
              <button type="button" onClick={() => setPage(page + 1)} disabled={page >= shownPages} className="px-1.5 py-1 hover:bg-[var(--ks-surface)] disabled:opacity-40 border-l border-[var(--ks-border)]" data-testid="pg-next"><ChevronRight size={12}/></button>
              <button type="button" onClick={() => setPage(shownPages)} disabled={page >= shownPages} className="px-2 py-1 hover:bg-[var(--ks-surface)] disabled:opacity-40 border-l border-[var(--ks-border)]" data-testid="pg-last">Last</button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="ks-table" data-testid="txs-table">
            <thead>
              <tr className="text-[var(--ks-text-soft)] text-xs">
                <th className="w-8">
                  <HoverTip label="View transaction"><span className="inline-flex"><HelpCircle size={12} /></span></HoverTip>
                </th>
                <th>Transaction Hash</th>
                <th>
                  <span className="inline-flex items-center gap-1">Method
                    <HoverTip label="Function name executed for this transaction">
                      <span className="inline-flex"><HelpCircle size={11} /></span>
                    </HoverTip>
                  </span>
                </th>
                <th>Block</th>
                <th className="text-[var(--ks-primary)] cursor-pointer" title="Sort by age">
                  <span className="inline-flex items-center gap-1">Age
                    <HoverTip label="Click to switch between Age and Date/Time (UTC)">
                      <span className="inline-flex"><HelpCircle size={11} /></span>
                    </HoverTip>
                  </span>
                </th>
                <th>From</th>
                <th>To</th>
                <th className="text-right text-[var(--ks-primary)] cursor-pointer">Amount</th>
                <th className="text-right text-[var(--ks-primary)] cursor-pointer">Txn Fee</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => {
                const fromLabel = minerLabel(t.from_address);
                return (
                  <tr key={t.hash} data-testid={`txs-row-${t.hash}`}>
                    <td>
                      <Link to={`/tx/${t.hash}`} className="text-[var(--ks-text-soft)] hover:text-[var(--ks-primary)]" title="View">
                        <Eye size={14} />
                      </Link>
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5">
                        {t.status === "Failed" && (
                          <HoverTip label={tips.failed}>
                            <span className="inline-flex items-center text-red-600 cursor-help" data-testid={`fail-icon-${t.hash}`}>
                              <AlertCircle size={14} strokeWidth={2.5} />
                            </span>
                          </HoverTip>
                        )}
                        {t.status === "Pending" && (
                          <HoverTip label={tips.pending}>
                            <span className="inline-flex items-center text-amber-500 cursor-help" data-testid={`pending-icon-${t.hash}`}>
                              <AlertCircle size={14} strokeWidth={2.5} />
                            </span>
                          </HoverTip>
                        )}
                        <Link to={`/tx/${t.hash}`} className="ks-link mono">{t.hash.slice(0, 10)}…</Link>
                        <InlineCopy text={t.hash} />
                      </div>
                    </td>
                    <td>
                      <span className="inline-block border border-[var(--ks-border)] rounded-md px-2 py-0.5 text-xs bg-[var(--ks-surface)] text-[var(--ks-text)]">
                        {t.method}
                      </span>
                    </td>
                    <td><Link to={`/block/${t.block_number}`} className="ks-link">{formatNumber(t.block_number)}</Link></td>
                    <td className="whitespace-nowrap text-[var(--ks-text-soft)]">{timeAgo(t.timestamp)}</td>
                    <td className="whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <Link to={`/address/${t.from_address}`} className="mono text-xs text-[var(--ks-text)] hover:text-[var(--ks-primary)]">
                          {shortChecksum(t.from_address, 8, 6)}
                        </Link>
                        <InlineCopy text={t.from_address} />
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <Link to={`/address/${t.to_address}`} className="mono text-xs text-[var(--ks-text)] hover:text-[var(--ks-primary)]">
                          {shortChecksum(t.to_address, 8, 6)}
                        </Link>
                        <InlineCopy text={t.to_address} />
                      </div>
                    </td>
                    <td className="text-right whitespace-nowrap">{formatEth(t.value_eth, 8)} ETH</td>
                    <td className="text-right whitespace-nowrap text-[var(--ks-text-soft)]">{formatEth(t.fee_eth, 8)}</td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={9} className="text-center py-8 text-[var(--ks-text-soft)]">No transactions found.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Bottom pagination mirror */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--ks-border)] text-xs" data-testid="txs-footer">
          <span className="text-[var(--ks-text-soft)]">Showing {(page - 1) * perPage + 1}-{Math.min(page * perPage, totalReal)} of {formatNumber(totalReal)} recent</span>
          <div className="inline-flex items-center gap-1 border border-[var(--ks-border)] rounded-md overflow-hidden">
            <button type="button" onClick={() => setPage(1)} disabled={page <= 1} className="px-2 py-1 hover:bg-[var(--ks-surface)] disabled:opacity-40">First</button>
            <button type="button" onClick={() => setPage(page - 1)} disabled={page <= 1} className="px-1.5 py-1 hover:bg-[var(--ks-surface)] disabled:opacity-40 border-l border-[var(--ks-border)]"><ChevronLeft size={12}/></button>
            <span className="px-2 py-1 text-[var(--ks-text-soft)] border-l border-[var(--ks-border)]">Page <span className="text-[var(--ks-text)] font-medium">{page}</span> of {shownPages}</span>
            <button type="button" onClick={() => setPage(page + 1)} disabled={page >= shownPages} className="px-1.5 py-1 hover:bg-[var(--ks-surface)] disabled:opacity-40 border-l border-[var(--ks-border)]"><ChevronRight size={12}/></button>
            <button type="button" onClick={() => setPage(shownPages)} disabled={page >= shownPages} className="px-2 py-1 hover:bg-[var(--ks-surface)] disabled:opacity-40 border-l border-[var(--ks-border)]">Last</button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
